import {Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, firstValueFrom, Subject, Subscription} from 'rxjs';
import {distinctUntilChanged, finalize} from 'rxjs/operators';
import {Centrifuge, Subscription as ChannelSubscription} from 'centrifuge';
import {environment} from '../../../environments/environment';
import {TokenDataProviderService} from './token-data-provider.service';
import {NetworkStatusService} from './network-status.service';
import {AppForegroundService} from './app-foreground.service';

export type PresenceState = 'online' | 'offline' | 'unknown';
export interface RealtimeGrant {channel: string; token: string; expiresAt: number;}
type Resource = {type: 'chat' | 'comments' | 'online' | 'userPresence' | 'health'; id?: string | number};
type Entry = {
  resource: Resource;
  refs: number;
  subscription?: ChannelSubscription;
  ready?: Promise<void>;
  presenceSynced?: boolean;
  revision?: number;
  releaseAt?: number;
};
export interface OnlineSnapshot {records: any[]; total: number; version: number;}

@Injectable({providedIn: 'root'})
export class EphemeralRealtimeService implements OnDestroy {
  private readonly lifecycle = new Subscription();
  readonly presence$ = new BehaviorSubject<Record<number, PresenceState>>({});
  readonly available$ = new BehaviorSubject(false);
  readonly rosterChanges$ = new Subject<any>();
  readonly synchronized$ = new Subject<void>();
  private client?: Centrifuge;
  private initialized = false;
  private userId: number | null = null;
  private generation = 0;
  private syncEpoch = 0;
  private entries = new Map<string, Entry>();
  private grants = new Map<string, RealtimeGrant>();
  private pendingGrants = new Map<string, {resource: Resource; resolve: (grant: RealtimeGrant) => void; reject: (error: any) => void}[]>();
  private grantTimer?: ReturnType<typeof setTimeout>;
  private startTimer?: ReturnType<typeof setTimeout>;
  private healthTimer?: ReturnType<typeof setTimeout>;
  private renewalTimer?: ReturnType<typeof setTimeout>;
  private sessionToken?: {token: string; expiresAt: number};
  private versions = new Map<number, number>();
  private typing = new Map<string, Map<string, {name: string; until: number}>>();
  private typingSubjects = new Map<string, BehaviorSubject<string[]>>();
  private lastTyping = new Map<string, number>();
  private typingTimer?: ReturnType<typeof setTimeout>;
  private userSyncTimer?: ReturnType<typeof setTimeout>;
  private userSyncRequest?: Subscription;
  private statusReleaseTimer?: ReturnType<typeof setTimeout>;
  private statusReleaseDeadline?: number;
  private headers = new HttpHeaders({'x-cache-bypass': 'true'});

  constructor(
    private readonly http: HttpClient,
    private readonly tokens: TokenDataProviderService,
    private readonly network: NetworkStatusService,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly foreground: AppForegroundService,
  ) {}

  initialize(): void {
    if (this.initialized || !isPlatformBrowser(this.platformId)) return;
    this.initialized = true;
    this.foreground.initialize();
    this.lifecycle.add(this.foreground.foreground$.pipe(distinctUntilChanged()).subscribe(active => {
      if (active) this.resume();
      else {
        this.invalidateUserSync();
        for (const channel of this.lastTyping.keys()) this.publishTyping(channel, false);
        this.clearTyping();
      }
    }));
    this.lifecycle.add(this.tokens.sessionChanged$.subscribe(() => { this.reset(); this.userId = null; }));
    this.lifecycle.add(this.tokens.user$.subscribe(user => {
      const id = user?.id || null;
      if (id === this.userId) return;
      this.reset(); this.userId = id;
      if (id) void this.start(this.generation);
    }));
    this.lifecycle.add(this.network.status$.subscribe(state => {
      if (!this.userId) return;
      if (!state.connected) { this.client?.disconnect(); this.unavailable(); }
      else if (this.client) this.client.connect();
      else void this.start(this.generation);
    }));
  }

  resume(): void {
    if (!isPlatformBrowser(this.platformId) || !this.foreground.isForeground || !this.userId || this.network.isOffline()) return;
    this.unavailable();
    if (this.client?.state === 'connected') {
      this.synchronized$.next(); this.scheduleUserSync();
    } else if (this.client) this.client.connect();
    else void this.start(this.generation);
  }

  private async start(generation: number): Promise<void> {
    if (this.client || generation !== this.generation || !this.userId || this.network.isOffline()) return;
    // Reserve the attempt so a simultaneous network notification cannot start a second connection.
    if (this.startTimer) return;
    const marker = setTimeout(() => {}, 2147483647);
    this.startTimer = marker;
    try {
      const session: any = await firstValueFrom(this.http.post(environment.apiUrl + '/realtime/session', {}, {headers: this.headers}));
      if (generation !== this.generation || !session.enabled) return;
      this.sessionToken = session;
      const client = new Centrifuge(session.websocketUrl, {
        token: session.token,
        getToken: async () => {
          if (generation !== this.generation) throw new Error('Realtime session ended');
          if (this.sessionToken && this.sessionToken.expiresAt * 1000 > Date.now() + 30000) return this.sessionToken.token;
          const value: any = await firstValueFrom(this.http.post(environment.apiUrl + '/realtime/session', {}, {headers: this.headers}));
          if (generation !== this.generation) throw new Error('Realtime session ended');
          if (!value.enabled) { this.reset(); throw new Error('Realtime disabled'); }
          this.sessionToken = value; this.scheduleRenewal(generation, value.expiresAt);
          this.updatePresenceGrant(value.presence);
          return value.token;
        },
        minReconnectDelay: 1000, maxReconnectDelay: 30000,
      });
      this.client = client;
      client.on('connected', () => this.zone.run(() => {
        if (generation !== this.generation || this.client !== client) return;
        this.synchronized$.next();
        this.scheduleUserSync();
      }));
      const unavailable = () => {
        if (generation === this.generation && this.client === client) this.zone.run(() => this.unavailable());
      };
      client.on('connecting', unavailable);
      client.on('disconnected', unavailable);
      this.updatePresenceGrant(session.presence);
      this.retain('health:presence', {type: 'health'});
      for (const [channel, entry] of this.entries) this.bind(channel, entry);
      client.connect();
      this.scheduleRenewal(generation, session.expiresAt);
    } catch {
      if (generation === this.generation && this.userId) {
        clearTimeout(marker);
        this.startTimer = setTimeout(() => { this.startTimer = undefined; void this.start(generation); }, 10000 + Math.random() * 10000);
        return;
      }
    } finally {
      // An old account request must not clear the next account's in-flight attempt.
      clearTimeout(marker);
      if (this.startTimer === marker) this.startTimer = undefined;
    }
  }

  private scheduleRenewal(generation: number, expiresAt: number): void {
    clearTimeout(this.renewalTimer);
    // One batched refresh per tab, spread over a 30 second window before expiry.
    this.renewalTimer = setTimeout(() => void this.renew(generation),
      Math.max(1000, expiresAt * 1000 - Date.now() - 60000 - Math.random() * 30000));
  }

  private async renew(generation: number): Promise<void> {
    if (generation !== this.generation || !this.userId) return;
    try {
      const session: any = await firstValueFrom(this.http.post(environment.apiUrl + '/realtime/session', {}, {headers: this.headers}));
      if (generation !== this.generation) return;
      if (!session.enabled) { this.reset(); return; }
      this.sessionToken = session; this.updatePresenceGrant(session.presence);
      this.scheduleRenewal(generation, session.expiresAt);
      const resources = [...this.entries].filter(([channel]) => channel !== 'presence:global').map(([, entry]) => entry.resource);
      if (!resources.length) return;
      const grants = await this.fetchGrants(resources);
      if (generation !== this.generation) return;
      for (const grant of grants) {
        if (this.entries.has(grant.channel)) this.grants.set(grant.channel, grant);
      }
    } catch {
      if (generation === this.generation) {
        clearTimeout(this.renewalTimer);
        this.renewalTimer = setTimeout(() => void this.renew(generation), 10000 + Math.random() * 10000);
      }
    }
  }

  private updatePresenceGrant(grant: RealtimeGrant | null): void {
    const channel = 'presence:global';
    if (!grant) {
      const previous = this.entries.get(channel);
      if (previous) { this.remove(channel); this.entries.delete(channel); }
      return;
    }
    this.grants.set(channel, grant);
    if (!this.entries.has(channel)) this.retain(channel, {type: 'health'});
  }

  watchUsers(ids: number[]): () => void {
    this.pruneReleasedStatuses();
    const unique = [...new Set(ids.filter(id => Number.isInteger(id) && id > 0))].slice(0, 50);
    const accepted: number[] = [];
    for (const id of unique) {
      if (!this.entries.has('status:' + id) && this.watchedIds().length >= 50 && !this.evictReleasedStatus()) continue;
      this.retain('status:' + id, {type: 'userPresence', id}); accepted.push(id);
    }
    this.scheduleStatusRelease();
    this.scheduleUserSync();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      accepted.forEach(id => this.release('status:' + id));
    };
  }

  private watchedIds(): number[] {
    return [...this.entries.keys()].filter(key => key.startsWith('status:')).map(key => Number(key.slice(7)));
  }

  private retain(channel: string, resource: Resource): Entry {
    let entry = this.entries.get(channel);
    if (entry) { entry.refs++; entry.releaseAt = undefined; return entry; }
    entry = {resource, refs: 1}; this.entries.set(channel, entry);
    if (this.client) this.bind(channel, entry);
    return entry;
  }

  private release(channel: string): void {
    const entry = this.entries.get(channel);
    if (!entry || --entry.refs > 0) return;
    if (channel.startsWith('status:') && isPlatformBrowser(this.platformId)) {
      entry.releaseAt = Date.now() + 10000;
      this.scheduleStatusRelease();
      return;
    }
    this.dropEntry(channel);
  }

  private dropEntry(channel: string): void {
    this.remove(channel); this.entries.delete(channel); this.grants.delete(channel);
    if (channel.startsWith('typing_')) {
      this.typing.delete(channel); this.typingSubjects.get(channel)?.next([]); this.lastTyping.delete(channel);
      this.typingSubjects.delete(channel);
      this.scheduleTypingExpiry();
    }
    if (channel.startsWith('status:')) {
      const id = Number(channel.slice(7));
      const states = {...this.presence$.value}; delete states[id];
      this.versions.delete(id); this.presence$.next(states);
      if (!this.watchedIds().length) this.invalidateUserSync();
    }
  }

  private evictReleasedStatus(): boolean {
    let oldest: string | undefined;
    let deadline = Infinity;
    for (const [channel, entry] of this.entries) {
      if (channel.startsWith('status:') && entry.refs === 0 && entry.releaseAt < deadline) {
        oldest = channel;
        deadline = entry.releaseAt;
      }
    }
    if (!oldest) return false;
    this.dropEntry(oldest);
    return true;
  }

  private pruneReleasedStatuses(): void {
    for (const [channel, entry] of this.entries) {
      if (channel.startsWith('status:') && entry.refs === 0 && entry.releaseAt <= Date.now()) this.dropEntry(channel);
    }
    this.scheduleStatusRelease();
  }

  private scheduleStatusRelease(): void {
    let deadline = Infinity;
    this.entries.forEach(entry => { if (entry.refs === 0 && entry.releaseAt !== undefined) deadline = Math.min(deadline, entry.releaseAt); });
    if (this.statusReleaseTimer !== undefined && deadline === this.statusReleaseDeadline) return;
    clearTimeout(this.statusReleaseTimer); this.statusReleaseTimer = undefined;
    this.statusReleaseDeadline = undefined;
    if (deadline === Infinity) return;
    this.statusReleaseDeadline = deadline;
    this.zone.runOutsideAngular(() => {
      this.statusReleaseTimer = setTimeout(() => {
        this.statusReleaseTimer = undefined;
        this.statusReleaseDeadline = undefined;
        this.zone.run(() => this.pruneReleasedStatuses());
      }, Math.max(0, deadline - Date.now()));
    });
  }

  private remove(channel: string): void {
    const entry = this.entries.get(channel);
    if (entry?.subscription) {
      entry.subscription.unsubscribe();
      this.client?.removeSubscription(entry.subscription);
      entry.subscription = undefined;
    }
  }

  private bind(channel: string, entry: Entry): void {
    if (!this.client || entry.subscription) return;
    const subscription = this.client.newSubscription(channel, {getToken: async () => (await this.grant(channel, entry.resource)).token});
    entry.subscription = subscription;
    subscription.on('publication', ctx => {
      if (this.entries.get(channel) === entry && entry.subscription === subscription) this.zone.run(() => this.receive(channel, ctx));
    });
    subscription.on('subscribing', () => {
      if (this.entries.get(channel) !== entry || entry.subscription !== subscription) return;
      if (channel.startsWith('status:')) {
        entry.presenceSynced = false;
        entry.revision = (entry.revision || 0) + 1;
        this.zone.run(() => this.setPresence(Number(channel.slice(7)), 'unknown', -1));
      }
    });
    subscription.on('subscribed', () => this.zone.run(() => {
      if (this.entries.get(channel) !== entry || entry.subscription !== subscription) return;
      if (channel.startsWith('status:')) this.scheduleUserSync();
      if (channel === 'roster:online') this.synchronized$.next();
    }));
    subscription.subscribe();
  }

  private grant(channel: string, resource: Resource): Promise<RealtimeGrant> {
    if (channel === 'presence:global') {
      const cachedPresence = this.grants.get(channel);
      if (cachedPresence && cachedPresence.expiresAt * 1000 > Date.now() + 60000) return Promise.resolve(cachedPresence);
      const generation = this.generation;
      return firstValueFrom(this.http.post<any>(environment.apiUrl + '/realtime/session', {}, {headers: this.headers})).then(session => {
        if (generation !== this.generation || !this.userId || !session.enabled) throw new Error('Realtime session ended');
        this.updatePresenceGrant(session.presence);
        if (!session.presence) throw new Error('Presence is hidden');
        return session.presence;
      });
    }
    const cached = this.grants.get(channel);
    if (cached && cached.expiresAt * 1000 > Date.now() + 60000 + Math.random() * 30000) return Promise.resolve(cached);
    return new Promise((resolve, reject) => {
      const pending = this.pendingGrants.get(channel) || [];
      pending.push({resource, resolve, reject}); this.pendingGrants.set(channel, pending);
      if (!this.grantTimer) this.grantTimer = setTimeout(() => void this.flushGrants(), 40);
    });
  }

  private async flushGrants(): Promise<void> {
    this.grantTimer = undefined;
    const pending = this.pendingGrants; this.pendingGrants = new Map();
    const generation = this.generation;
    try {
      const grants = await this.fetchGrants([...pending.values()].map(values => values[0].resource));
      if (generation !== this.generation) throw new Error('Session changed');
      const returned = new Set<string>();
      for (const grant of grants) {
        returned.add(grant.channel);
        if (this.entries.has(grant.channel)) this.grants.set(grant.channel, grant);
        pending.get(grant.channel)?.forEach(request => request.resolve(grant));
      }
      pending.forEach((values, channel) => {
        if (!returned.has(channel)) values.forEach(request => request.reject(new Error('Missing channel grant')));
      });
    } catch (error) { pending.forEach(values => values.forEach(request => request.reject(error))); }
  }

  private async fetchGrants(resources: Resource[]): Promise<RealtimeGrant[]> {
    const requests: Promise<{subscriptions: RealtimeGrant[]}>[] = [];
    for (let index = 0; index < resources.length; index += 64) {
      requests.push(firstValueFrom(this.http.post<{subscriptions: RealtimeGrant[]}>(environment.apiUrl + '/realtime/subscriptions', {
        resources: resources.slice(index, index + 64),
      }, {headers: this.headers})));
    }
    return (await Promise.all(requests)).flatMap(response => response.subscriptions);
  }

  private pendingUsers(): Array<{id: number; entry: Entry; revision: number}> {
    return this.watchedIds().flatMap(id => {
      const entry = this.entries.get('status:' + id)!;
      return entry.refs > 0 && entry.subscription?.state === 'subscribed' && !entry.presenceSynced ? [{id, entry, revision: entry.revision || 0}] : [];
    });
  }

  private scheduleUserSync(): void {
    if (this.userSyncTimer || this.userSyncRequest || !this.foreground.isForeground || !this.userId || this.network.isOffline() || !this.pendingUsers().length) return;
    this.zone.runOutsideAngular(() => {
      this.userSyncTimer = setTimeout(() => { this.userSyncTimer = undefined; this.resyncUsers(); }, 60);
    });
  }

  private invalidateUserSync(): void {
    this.syncEpoch++;
    clearTimeout(this.userSyncTimer); this.userSyncTimer = undefined;
    this.entries.forEach(entry => { entry.presenceSynced = false; });
    const request = this.userSyncRequest;
    this.userSyncRequest = undefined;
    request?.unsubscribe();
  }

  private resyncUsers(): void {
    if (this.userSyncRequest || !this.foreground.isForeground || !this.userId || this.network.isOffline()) return;
    const pending = this.pendingUsers();
    if (!pending.length) return;
    const ids = pending.map(item => item.id);
    const generation = this.generation;
    const epoch = this.syncEpoch;
    const request = new Subscription();
    this.userSyncRequest = request;
    let received = false;
    request.add(this.http.post<{statuses: Record<string, boolean>; version: number}>(environment.apiUrl + '/users/presence', {ids}, {headers: this.headers}).pipe(
      finalize(() => {
        if (this.userSyncRequest !== request) return;
        this.userSyncRequest = undefined;
        this.scheduleUserSync();
      }),
    ).subscribe({
      next: response => this.zone.run(() => {
        if (generation !== this.generation || epoch !== this.syncEpoch || !this.foreground.isForeground) return;
        received = true;
        this.confirmHealth();
        pending.forEach(({id, entry, revision}) => {
          if (this.entries.get('status:' + id) !== entry || (entry.revision || 0) !== revision || entry.subscription?.state !== 'subscribed') return;
          entry.presenceSynced = true;
          this.setPresence(id, response.statuses[id] ? 'online' : 'offline', response.version);
        });
      }),
      error: () => {
        if (generation === this.generation && epoch === this.syncEpoch) this.zone.run(() => this.unavailable());
      },
      complete: () => {
        if (!received && generation === this.generation && epoch === this.syncEpoch) this.zone.run(() => this.unavailable());
      },
    }));
  }

  private receive(channel: string, context: any): void {
    const data = context.data;
    if (channel === 'health:presence' && data?.available === true) {
      const recovering = !this.available$.value;
      this.confirmHealth();
      if (recovering) { this.synchronized$.next(); this.scheduleUserSync(); }
    } else if (channel.startsWith('status:') && typeof data?.online === 'boolean' && Number.isSafeInteger(data.version)) {
      this.setPresence(Number(channel.slice(7)), data.online ? 'online' : 'offline', data.version);
    } else if (channel === 'roster:online') this.rosterChanges$.next(data);
    else if (channel.startsWith('typing_') && typeof data?.active === 'boolean') {
      if (!this.foreground.isForeground || !this.entries.has(channel)) return;
      const info = context.info;
      if (!info?.user || info.user === String(this.userId) || !info.client) return;
      const actors = this.typing.get(channel) || new Map();
      const key = info.user + ':' + info.client;
      if (data.active) actors.set(key, {name: info.chanInfo?.username || info.channel_info?.username || info.connInfo?.username || info.conn_info?.username || '', until: Date.now() + 7000});
      else actors.delete(key);
      if (actors.size) this.typing.set(channel, actors); else this.typing.delete(channel);
      this.emitTyping(channel);
      this.scheduleTypingExpiry();
    }
  }

  private confirmHealth(): void {
    this.available$.next(true);
    clearTimeout(this.healthTimer);
    this.healthTimer = setTimeout(() => this.zone.run(() => this.unavailable()), 25000);
  }

  private setPresence(id: number, state: PresenceState, version: number): void {
    if (!this.entries.has('status:' + id)) return;
    if (version >= 0 && version < (this.versions.get(id) ?? -1)) return;
    if (version >= 0) this.versions.set(id, version); else this.versions.delete(id);
    if (this.presence$.value[id] !== state) this.presence$.next({...this.presence$.value, [id]: state});
  }

  private unavailable(): void {
    this.invalidateUserSync();
    this.available$.next(false);
    const states: Record<number, PresenceState> = {};
    this.watchedIds().forEach(id => states[id] = 'unknown');
    this.presence$.next(states); this.versions.clear();
    this.clearTyping();
  }

  typingNames(channel: string): BehaviorSubject<string[]> {
    if (!this.typingSubjects.has(channel)) this.typingSubjects.set(channel, new BehaviorSubject<string[]>([]));
    return this.typingSubjects.get(channel)!;
  }

  watchTyping(channel: string, stopOnRelease = true): () => void {
    const match = /^typing_(chat|comments):([a-zA-Z0-9-]+)$/.exec(channel || '');
    if (!match) return () => {};
    this.retain(channel, {type: match[1] as 'chat' | 'comments', id: match[2]});
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (stopOnRelease) this.publishTyping(channel, false);
      this.release(channel);
    };
  }

  publishTyping(channel: string, active: boolean): void {
    if (active && !this.foreground.isForeground) return;
    const subscription = this.entries.get(channel)?.subscription;
    if (!subscription || subscription.state !== 'subscribed' || !this.available$.value) return;
    const last = this.lastTyping.get(channel);
    if (active && last && Date.now() - last < 5000) return;
    if (!active && !last) return;
    if (active) this.lastTyping.set(channel, Date.now()); else this.lastTyping.delete(channel);
    void subscription.publish({active}).catch(() => {});
  }

  private expireTyping(): void {
    this.typing.forEach((actors, channel) => {
      let changed = false;
      actors.forEach((actor, key) => { if (actor.until <= Date.now()) { actors.delete(key); changed = true; } });
      if (!actors.size) this.typing.delete(channel);
      if (changed) this.zone.run(() => this.emitTyping(channel));
    });
    this.scheduleTypingExpiry();
  }

  private scheduleTypingExpiry(): void {
    clearTimeout(this.typingTimer); this.typingTimer = undefined;
    if (!this.foreground.isForeground) return;
    let deadline = Infinity;
    this.typing.forEach(actors => actors.forEach(actor => { deadline = Math.min(deadline, actor.until); }));
    if (deadline === Infinity) return;
    this.zone.runOutsideAngular(() => {
      this.typingTimer = setTimeout(() => { this.typingTimer = undefined; this.expireTyping(); }, Math.max(0, deadline - Date.now()));
    });
  }

  private clearTyping(): void {
    clearTimeout(this.typingTimer); this.typingTimer = undefined;
    this.typing.clear(); this.typingSubjects.forEach(subject => subject.next([]));
    this.lastTyping.clear();
  }

  private emitTyping(channel: string): void {
    this.typingNames(channel).next([...new Set([...this.typing.get(channel)?.values() || []].map(actor => actor.name).filter(Boolean))]);
  }

  watchRoster(): () => void {
    this.retain('roster:online', {type: 'online'});
    return () => this.release('roster:online');
  }

  snapshot(): Promise<OnlineSnapshot> {
    return firstValueFrom(this.http.get<OnlineSnapshot>(environment.apiUrl + '/users/online', {headers: this.headers}));
  }

  ngOnDestroy(): void {
    this.lifecycle.unsubscribe();
    this.reset();
    this.userId = null;
    this.entries.clear(); this.versions.clear(); this.presence$.next({});
    this.typingSubjects.forEach(subject => subject.complete()); this.typingSubjects.clear();
  }

  private reset(): void {
    this.generation++;
    clearTimeout(this.statusReleaseTimer); this.statusReleaseTimer = undefined;
    this.statusReleaseDeadline = undefined;
    for (const [channel, entry] of this.entries) {
      if (channel.startsWith('status:') && entry.refs === 0) this.dropEntry(channel);
    }
    clearTimeout(this.userSyncTimer); this.userSyncTimer = undefined;
    clearTimeout(this.grantTimer); this.grantTimer = undefined;
    this.pendingGrants.forEach(values => values.forEach(request => request.reject(new Error('Session changed'))));
    this.pendingGrants.clear();
    clearTimeout(this.startTimer); this.startTimer = undefined;
    clearTimeout(this.healthTimer);
    clearTimeout(this.renewalTimer); this.sessionToken = undefined;
    this.client?.disconnect(); this.client = undefined;
    this.entries.forEach(entry => { entry.subscription = undefined; });
    this.entries.delete('presence:global'); this.entries.delete('health:presence');
    this.grants.clear(); this.unavailable();
  }
}
